import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import { Line } from "react-chartjs-2";
import {
  FiDatabase, FiMail, FiHardDrive, FiUsers, FiActivity,
} from "react-icons/fi";
import { useAuth } from '../lib/StAuthGiver';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Filler, Tooltip,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv !== 'aws');

const getPastDays = (days) => {
  const daysList = [];
  for (let ii = days; ii > 0; ii--) {
    const d = new Date();
    d.setDate(d.getDate() - ii);
    daysList.push(d.toISOString().split('T')[0]);
  }
  return daysList;
};

export const StDashLanding = () => {
  const { loginAction, logOut } = useAuth();
  let params = useParams();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(undefined);
  const [demo, setDemo] = useState(false);
  const [dateRange, setDateRange] = useState('7');
  const isMounted = useRef(false);
  const curSiteId = getCurSiteId();

  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    const fetchData = async () => {
      const rcJson = await callApi('/sites/' + curSiteId + '/stats', 'GET', {}, {});
      Tdebug && console.log(rcJson);

      if (rcJson.disposition === 'logout') { logOut(); return; }

      if (rcJson.httpStatus != 200) {
        if (rcJson.disposition === 'empty') { setDemo(true); }
        if (rcJson.respBody?.etac) loginAction(null, rcJson.etac);
      } else {
        if (rcJson.respBody?.etac) loginAction(null, rcJson.etac);
        setStats(rcJson.message);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const generateChartData = (range) => {
    const days = parseInt(range);
    const labels = getPastDays(days);
    let chartData = new Array(days).fill(0);

    if (!demo && stats) {
      for (let ii = 0; ii < days; ii++) {
        if (labels[ii] in (stats.siteVisitData?.weeklyWindow || {})) {
          chartData[ii] = stats.siteVisitData.weeklyWindow[labels[ii]];
        }
      }
    } else {
      chartData = chartData.map(() => Math.floor(Math.random() * 400) + 100);
    }

    return {
      labels: labels.map(date => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      data: chartData,
    };
  };

  const chartData = generateChartData(dateRange);

  const data = {
    labels: chartData.labels,
    datasets: [{
      label: 'Visitors',
      data: chartData.data,
      fill: true,
      backgroundColor: 'rgba(37, 99, 235, 0.05)',
      borderColor: '#2563eb',
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHoverBackgroundColor: '#2563eb',
      pointHoverBorderColor: '#ffffff',
      pointHoverBorderWidth: 2,
      tension: 0.3,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#18181b',
        titleColor: '#fafafa',
        bodyColor: '#a1a1aa',
        borderWidth: 0,
        cornerRadius: 6,
        padding: 10,
        displayColors: false,
        titleFont: { size: 12, weight: '600' },
        bodyFont: { size: 11 },
        callbacks: {
          label: (ctx) => `${ctx.parsed.y.toLocaleString()} visitors`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'var(--border-subtle)', drawBorder: false },
        border: { display: false },
        ticks: { color: 'var(--text-tertiary)', font: { size: 10 }, padding: 8, maxTicksLimit: 4 },
      },
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: 'var(--text-tertiary)', font: { size: 10 }, padding: 8 },
      },
    },
  };

  const statCards = [
    {
      icon: FiDatabase,
      value: stats ? (Number(stats.bwMonth) / 1000000).toFixed(1) : '0',
      unit: 'MB',
      label: 'Data usage',
      trend: null,
    },
    {
      icon: FiMail,
      value: stats ? (Number(stats.cfForMonth) || 0).toLocaleString() : '0',
      label: 'Form submissions',
      trend: null,
    },
    {
      icon: FiHardDrive,
      value: stats ? (Number(stats.totalAssetSizeMb) || 0).toFixed(1) : '0',
      unit: 'MB',
      label: 'Assets',
      trend: null,
    },
    {
      icon: FiUsers,
      value: stats ? (Number(stats.siteVisitData?.visitsThisMonth) || 0).toLocaleString() : '0',
      label: 'Monthly visitors',
      trend: null,
    },
    {
      icon: FiActivity,
      value: stats ? (Number(stats.siteVisitData?.visitsThisWeek) || 0).toLocaleString() : '0',
      label: 'Weekly visitors',
      trend: null,
    },
  ];

  if (loading) {
    return (
      <div className="app-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner animation="border" style={{ width: '2rem', height: '2rem', color: 'var(--accent-primary)' }} />
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-tertiary)' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-content animate-in">
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          Dashboard
          {demo && <span className="dashboard-badge">Demo</span>}
        </h1>
        <p className="dashboard-subtitle">Website performance overview</p>
      </div>

      <div className="stats-row">
        {statCards.map((card, idx) => (
          <div key={idx} className="stat-card-inline">
            <div className="stat-label">
              <card.icon />
              {card.label}
            </div>
            <div className="stat-value">
              {card.value}
              {card.unit && <span> {card.unit}</span>}
              {card.trend && (
                <span className={`stat-trend ${card.up ? 'stat-trend--up' : 'stat-trend--down'}`}>
                  {card.trend}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="chart-section">
        <div className="section-header">
          <h3 className="section-title">Visitor Analytics</h3>
          <select
            className="chart-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
          </select>
        </div>
        <div className="chart-container">
          <Line data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default StDashLanding;
